import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  CardActions,
  Grid
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ExitToApp as LeaveIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  Photo as PhotoIcon,
  Article as ArticleIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  PhoneAndroid as PhoneIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { accountDeletionService } from '../services/accountDeletionService';
import { familyCodeService } from '../services/familyCodeService';
import { twoFactorService } from '../services/twoFactorService';
import TwoFactorSetup from './TwoFactorSetup';
import logger from '../utils/logger';

const AccountSettings = () => {
  const { currentUser, logout } = useAuth();
  const { profile, refreshProfile } = useProfile();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletionStatus, setDeletionStatus] = useState(null);

  // Delete Account Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteImmediately, setDeleteImmediately] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  // Leave Family Dialog
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveConfirm, setLeaveConfirm] = useState(false);

  // Export Data Dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [exporting, setExporting] = useState(false);

  // 2FA State
  const [twoFactorSetupOpen, setTwoFactorSetupOpen] = useState(false);
  const [enrolledFactors, setEnrolledFactors] = useState([]);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);

  // Load deletion status on mount
  useEffect(() => {
    const loadDeletionStatus = async () => {
      if (currentUser?.uid) {
        try {
          const status = await accountDeletionService.getDeletionStatus(currentUser.uid);
          setDeletionStatus(status);
        } catch (err) {
          logger.error('Error loading deletion status:', err);
        }
      }
    };
    loadDeletionStatus();
  }, [currentUser?.uid]);

  // Load 2FA enrolled factors
  useEffect(() => {
    const loadEnrolledFactors = () => {
      if (currentUser) {
        const factors = twoFactorService.getEnrolledFactors();
        setEnrolledFactors(factors);
      }
    };
    loadEnrolledFactors();
  }, [currentUser]);

  // Handle 2FA enrollment completion
  const handleTwoFactorComplete = () => {
    setTwoFactorSetupOpen(false);
    const factors = twoFactorService.getEnrolledFactors();
    setEnrolledFactors(factors);
    setSuccess('Two-factor authentication has been enabled.');
  };

  // Handle 2FA unenroll
  const handleUnenroll2FA = async (factorUid) => {
    setTwoFactorLoading(true);
    setError('');

    try {
      await twoFactorService.unenroll(factorUid);
      const factors = twoFactorService.getEnrolledFactors();
      setEnrolledFactors(factors);
      setSuccess('Two-factor authentication has been disabled.');
    } catch (err) {
      logger.error('Error disabling 2FA:', err);
      setError(err.message || 'Failed to disable two-factor authentication.');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setError('Please enter your password');
      return;
    }

    if (!deleteConfirm) {
      setError('Please confirm that you understand this action');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Verify password
      await signInWithEmailAndPassword(auth, currentUser.email, deletePassword);

      if (deleteImmediately) {
        // Immediate deletion
        await accountDeletionService.deleteAccountImmediately(currentUser.uid, currentUser);
        await logout();
        navigate('/login');
      } else {
        // Schedule deletion with 30-day grace period
        const request = await accountDeletionService.scheduleAccountDeletion(currentUser.uid, {
          gracePeriodDays: 30,
          reason: deleteReason
        });

        setDeletionStatus(request);
        setSuccess('Your account has been scheduled for deletion. You can cancel this within the next 30 days.');
        setDeleteDialogOpen(false);
        resetDeleteForm();
      }
    } catch (err) {
      logger.error('Error deleting account:', err);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Incorrect password. Please try again.');
      } else {
        setError(err.message || 'Failed to delete account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Cancel scheduled deletion
  const handleCancelDeletion = async () => {
    setLoading(true);
    setError('');

    try {
      await accountDeletionService.cancelAccountDeletion(currentUser.uid);
      setDeletionStatus(null);
      setSuccess('Account deletion has been cancelled.');
      if (refreshProfile) {
        await refreshProfile();
      }
    } catch (err) {
      logger.error('Error cancelling deletion:', err);
      setError(err.message || 'Failed to cancel deletion. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle leave family
  const handleLeaveFamily = async () => {
    if (!leaveConfirm) {
      setError('Please confirm that you want to leave');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await familyCodeService.leaveFamily(currentUser.uid);
      setSuccess('You have left the family. Your account remains active.');
      setLeaveDialogOpen(false);
      setLeaveConfirm(false);

      if (refreshProfile) {
        await refreshProfile();
      }

      // Redirect to profile after a short delay
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (err) {
      logger.error('Error leaving family:', err);
      setError(err.message || 'Failed to leave family. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle export data
  const handleExportData = async () => {
    setExporting(true);
    setError('');

    try {
      const data = await accountDeletionService.exportUserData(currentUser.uid);
      setExportData(data);
      setExportDialogOpen(true);
    } catch (err) {
      logger.error('Error exporting data:', err);
      setError(err.message || 'Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Download exported data as JSON
  const downloadExportData = () => {
    if (!exportData) return;

    const dataStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `family-for-life-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const resetDeleteForm = () => {
    setDeletePassword('');
    setDeleteConfirm(false);
    setDeleteImmediately(false);
    setDeleteReason('');
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Check if user is family owner
  const isFamilyOwner = profile?.role === 'owner';

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Account Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Pending Deletion Warning */}
      {deletionStatus && deletionStatus.status === 'pending' && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleCancelDeletion}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <CancelIcon />}
            >
              Cancel Deletion
            </Button>
          }
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Account Scheduled for Deletion
          </Typography>
          <Typography variant="body2">
            Your account will be permanently deleted on {formatDate(deletionStatus.scheduledDeletionDate)}.
            You can cancel this at any time before the scheduled date.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Export Data Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <DownloadIcon color="primary" />
                <Typography variant="h6">Export Your Data</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Download a copy of all your data including profile information,
                family membership, relationships, and file metadata.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExportData}
                disabled={exporting || loading}
              >
                {exporting ? 'Preparing...' : 'Export Data'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Two-Factor Authentication Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <SecurityIcon color="primary" />
                <Typography variant="h6">Two-Factor Authentication</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Add an extra layer of security to your account by requiring a verification
                code from your phone when signing in.
              </Typography>

              {enrolledFactors.length > 0 ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Two-factor authentication is enabled
                  </Alert>
                  <List dense>
                    {enrolledFactors.map((factor) => (
                      <ListItem
                        key={factor.uid}
                        secondaryAction={
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleUnenroll2FA(factor.uid)}
                            disabled={twoFactorLoading || loading}
                          >
                            Remove
                          </Button>
                        }
                      >
                        <ListItemIcon>
                          <PhoneIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={factor.displayName || 'Phone'}
                          secondary={`Added ${factor.enrollmentTime ? new Date(factor.enrollmentTime).toLocaleDateString() : 'recently'}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Alert severity="info">
                  Two-factor authentication is not enabled. Enable it to protect your account.
                </Alert>
              )}
            </CardContent>
            <CardActions>
              {enrolledFactors.length === 0 && (
                <Button
                  variant="contained"
                  startIcon={<SecurityIcon />}
                  onClick={() => setTwoFactorSetupOpen(true)}
                  disabled={loading || twoFactorLoading}
                >
                  Enable 2FA
                </Button>
              )}
            </CardActions>
          </Card>
        </Grid>

        {/* Leave Family Section */}
        {profile?.familyCode && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <LeaveIcon color="warning" />
                  <Typography variant="h6">Leave Family</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Leave <strong>{profile.familyCode}</strong> while keeping your account.
                  You can join another family later.
                </Typography>
                {isFamilyOwner && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    As the family owner, you cannot leave. Transfer ownership first.
                  </Alert>
                )}
              </CardContent>
              <CardActions>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<LeaveIcon />}
                  onClick={() => setLeaveDialogOpen(true)}
                  disabled={loading || isFamilyOwner}
                >
                  Leave Family
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}

        {/* Delete Account Section */}
        <Grid item xs={12}>
          <Card sx={{ borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <DeleteIcon color="error" />
                <Typography variant="h6" color="error">
                  Delete Account
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Permanently delete your account and all associated data. This action cannot be undone.
              </Typography>
              <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>This will permanently delete:</strong>
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Your profile and account information" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <PeopleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Your family membership and relationships" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <PhotoIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="All photos and uploaded files" />
                  </ListItem>
                  <ListItem sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <ArticleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Documents and vault contents" />
                  </ListItem>
                </List>
              </Alert>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={loading || (deletionStatus?.status === 'pending')}
              >
                Delete My Account
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Account Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          resetDeleteForm();
          setError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Delete Your Account
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Are you absolutely sure?
            </Typography>
            <Typography variant="body2">
              This action will permanently delete all your data and cannot be undone.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Enter your password to confirm:
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            disabled={loading}
            sx={{ mb: 2 }}
            autoFocus
          />

          <TextField
            fullWidth
            label="Reason for leaving (optional)"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            disabled={loading}
            multiline
            rows={2}
            sx={{ mb: 2 }}
            placeholder="Help us improve by sharing why you're leaving..."
          />

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={deleteImmediately}
                onChange={(e) => setDeleteImmediately(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Delete immediately (skip 30-day grace period)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Otherwise, your account will be scheduled for deletion in 30 days
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.checked)}
                color="error"
                disabled={loading}
              />
            }
            label={
              <Typography variant="body2" color="error">
                I understand that all my data will be permanently deleted and this cannot be undone
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              resetDeleteForm();
              setError('');
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={loading || !deletePassword || !deleteConfirm}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {loading ? 'Processing...' : deleteImmediately ? 'Delete Now' : 'Schedule Deletion'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave Family Dialog */}
      <Dialog
        open={leaveDialogOpen}
        onClose={() => {
          setLeaveDialogOpen(false);
          setLeaveConfirm(false);
          setError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Leave Family
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              What happens when you leave:
            </Typography>
            <List dense>
              <ListItem sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText primary="Your account remains active" />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText primary="Your personal profile is preserved" />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <WarningIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText primary="You'll be removed from the family" />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <WarningIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText primary="You'll lose access to family content" />
              </ListItem>
            </List>
          </Alert>

          <Typography variant="body1" gutterBottom>
            Are you sure you want to leave <strong>{profile?.familyCode}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Family admins will be notified when you leave.
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={leaveConfirm}
                onChange={(e) => setLeaveConfirm(e.target.checked)}
                disabled={loading}
              />
            }
            label="I understand and want to leave this family"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLeaveDialogOpen(false);
              setLeaveConfirm(false);
              setError('');
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleLeaveFamily}
            disabled={loading || !leaveConfirm}
            startIcon={loading ? <CircularProgress size={20} /> : <LeaveIcon />}
          >
            {loading ? 'Leaving...' : 'Leave Family'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Data Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => {
          setExportDialogOpen(false);
          setExportData(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Your Data Export
        </DialogTitle>
        <DialogContent>
          {exportData && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your data has been prepared for download.
              </Alert>

              <Typography variant="subtitle2" gutterBottom>
                Export Summary:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Profile Information"
                    secondary={exportData.profile?.displayName || exportData.profile?.email}
                  />
                </ListItem>
                {exportData.familyMembership && (
                  <ListItem>
                    <ListItemIcon>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Family Membership"
                      secondary={`${exportData.familyMembership.familyCode} - ${exportData.familyMembership.role}`}
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemIcon>
                    <PeopleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Relationships"
                    secondary={`${exportData.relationships?.length || 0} relationships`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PhotoIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Photos"
                    secondary={`${exportData.photos?.length || 0} photos`}
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary">
                Export generated on {formatDate(exportData.exportedAt)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setExportDialogOpen(false);
              setExportData(null);
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={downloadExportData}
            disabled={!exportData}
          >
            Download JSON
          </Button>
        </DialogActions>
      </Dialog>

      {/* Two-Factor Setup Dialog */}
      <TwoFactorSetup
        open={twoFactorSetupOpen}
        onClose={() => setTwoFactorSetupOpen(false)}
        onComplete={handleTwoFactorComplete}
      />
    </Box>
  );
};

export default AccountSettings;
